import {
  LoaderFunction,
  Navigate,
  RouteObject,
  generatePath,
  redirect,
} from "react-router-dom";
import { SELECTED_DATE_PARAM_KEY } from "../hooks/useDateSearchParamOrFallbackToToday";
import ShipmentsTable from "../pages/inbound/shared/ShipmentsTable";
import { ErrorBoundary } from "../components/ErrorBoundary";
import { useIsFeatureFlagEnabled } from "../featureFlags/useIsFeatureFlagEnabled";
import type { InboundView } from "../types/inbound/inboundView.type";

const redirectWithSelectedDate =
  (path: string): LoaderFunction =>
  ({ request }) => {
    const url = new URL(request.url);
    const selectedDate = url.searchParams.get(SELECTED_DATE_PARAM_KEY);

    return selectedDate
      ? redirect(generatePath(path, { date: selectedDate }))
      : redirect(path);
  };

const createShipmentsTableRoute = (
  path: string,
  view: InboundView,
  options: {
    showPresetRouteColumn?: boolean;
    isDraggable?: boolean;
    defaultSort: { id: string; desc: boolean }[];
  }
): RouteObject => ({
  path,
  errorElement: <ErrorBoundary />,
  element: (
    <ShipmentsTable
      rowSelectionMode="checkbox"
      view={view}
      haveContextMenu
      showPresetRouteColumn={options.showPresetRouteColumn}
      isDraggable={options.isDraggable}
      defaultSort={options.defaultSort}
    />
  ),
});

const prePlanningChildrenRoutes = (): RouteObject[] => [
  { index: true, loader: redirectWithSelectedDate("appointments") },
  {
    path: "appointments",
    errorElement: <ErrorBoundary />,
    lazy: () =>
      import("../pages/inbound/prePlanning/tables/appointments/Appointments"),
  },
  createShipmentsTableRoute("appointments/:id/shipments", "appointments", {
    showPresetRouteColumn: true,
    defaultSort: [{ id: "presetRouteName", desc: false }],
  }),
  {
    path: "traps",
    errorElement: <ErrorBoundary />,
    lazy: () => import("../pages/inbound/prePlanning/tables/traps/Traps"),
  },
  createShipmentsTableRoute("traps/:id/shipments", "traps", {
    showPresetRouteColumn: true,
    isDraggable: true,
    defaultSort: [{ id: "presetRouteName", desc: false }],
  }),
  createShipmentsTableRoute("routes/:id/shipments", "routes", {
    isDraggable: true,
    defaultSort: [{ id: "consignee", desc: false }],
  }),
  {
    path: "sections",
    errorElement: <ErrorBoundary />,
    lazy: () => import("../pages/inbound/prePlanning/tables/sections/Sections"),
  },
  createShipmentsTableRoute("sections/:id/shipments", "sections", {
    showPresetRouteColumn: true,
    isDraggable: true,
    defaultSort: [{ id: "presetRouteName", desc: false }],
  }),
];

const inboundPlanningChildrenRoutes = (): RouteObject[] => [
  { index: true, loader: redirectWithSelectedDate("planning") },
  {
    path: "planning",
    errorElement: <ErrorBoundary />,
    lazy: () => import("../pages/inbound/inboundPlanning/InboundPlanning"),
    children: [
      { index: true, element: <Navigate to="list" replace /> },
      {
        path: "list",
        errorElement: <ErrorBoundary />,
        lazy: () => import("../pages/inbound/inboundPlanning/InboundListPage"),
      },
      {
        path: "grid",
        errorElement: <ErrorBoundary />,
        lazy: () => import("../pages/inbound/inboundPlanning/InboundGridPage"),
      },
      {
        path: "map",
        errorElement: <ErrorBoundary />,
        lazy: () => import("../pages/inbound/inboundPlanning/InboundMapPage"),
      },
    ],
  },
  {
    path: "planning/list/:id/shipments",
    errorElement: <ErrorBoundary />,
    lazy: () =>
      import("../pages/inbound/inboundPlanning/tables/PlanShipmentsTable"),
  },
  {
    path: "planning/list/search",
    errorElement: <ErrorBoundary />,
    lazy: () => import("../pages/inbound/inboundPlanning/search/Search"),
  },
];

const getInboundRoutes = (
  isNewUnplannedDesignEnabled: boolean
): RouteObject[] => [
  {
    path: "inbound",
    lazy: () => import("../pages/inbound/InboundLayout"),
    errorElement: <ErrorBoundary />,
    children: [
      { index: true, element: <Navigate to="unplanned-freight" replace /> },
      {
        path: "pre-planning",
        lazy: () => import("../pages/inbound/prePlanning/PrePlanning"),
        errorElement: <ErrorBoundary />,
        children: prePlanningChildrenRoutes(),
      },
      {
        path: "unplanned-freight",
        errorElement: <ErrorBoundary />,
        lazy: () =>
          import("../pages/inbound/unplannedFreight/UnplannedFreight"),
        ...(isNewUnplannedDesignEnabled
          ? {
              children: [
                { index: true, loader: redirectWithSelectedDate("linehaul") },
                {
                  path: "linehaul",
                  errorElement: <ErrorBoundary />,
                  lazy: () =>
                    import(
                      "../pages/inbound/unplannedFreight/linehaul/Linehaul"
                    ),
                },
                {
                  path: "traps",
                  errorElement: <ErrorBoundary />,
                  lazy: () =>
                    import("../pages/inbound/unplannedFreight/traps/Traps"),
                },
                {
                  path: "freight-on-dock",
                  errorElement: <ErrorBoundary />,
                  lazy: () =>
                    import(
                      "../pages/inbound/unplannedFreight/freightOnDock/FreightOnDock"
                    ),
                },
                {
                  path: "incomplete-trailers",
                  errorElement: <ErrorBoundary />,
                  lazy: () =>
                    import(
                      "../pages/inbound/unplannedFreight/incompleteTrailers/IncompleteTrailers"
                    ),
                },
                {
                  path: "shuttles",
                  errorElement: <ErrorBoundary />,
                  lazy: () =>
                    import(
                      "../pages/inbound/unplannedFreight/shuttles/Shuttles"
                    ),
                },
              ],
            }
          : {}),
      },
      {
        lazy: () =>
          import("../pages/inbound/inboundPlanning/InboundPlanningLayout"),
        errorElement: <ErrorBoundary />,
        children: inboundPlanningChildrenRoutes(),
      },
      {
        path: "dock-manager",
        errorElement: <ErrorBoundary />,
        lazy: () =>
          import("../pages/inbound/inboundDockManager/InboundDockManager"),
      },
      {
        path: "uc-cc",
        errorElement: <ErrorBoundary />,
        lazy: () =>
          import(
            "../pages/inbound/inboundUrgentAndCriticalCare/InboundUrgentAndCriticalCare"
          ),
      },
    ],
  },
];

export const useInboundRoutes = (): RouteObject[] => {
  const isNewUnplannedDesignEnabled = useIsFeatureFlagEnabled(
    "inbound-2967-unplanned-freight-new-design-client"
  );

  return getInboundRoutes(isNewUnplannedDesignEnabled);
};
